import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';
import { BooleanField, ShowButton } from 'react-admin';

import { CoachDetailsFragment, CoachType } from '../../../graphql/types';
import PaginationWaypoint from '../../reusable/PaginationWaypoint';

interface ListCoachesProps {
  loadMore: () => void;
  coaches?: CoachDetailsFragment[];
  clientId?: string;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
}
export const ListCoaches: React.FC<ListCoachesProps> = ({
  coaches,
  clientId,
  loadMore,
  hasNextPage,
  isFetchingMore,
}) => {
  const getDisplayType = (type?: CoachType | null) => {
    switch (type) {
      case CoachType.Dietitian:
        return 'Nutrition';
      case CoachType.PerformCoach:
        return 'Perform';
      case CoachType.CalendarOnly:
        return 'Calendar Only';
      default:
        return 'Coach';
    }
  };

  if (!coaches) {
    return null;
  }

  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Style</TableCell>
            <TableCell>Locations</TableCell>
            <TableCell>Countries</TableCell>
            {clientId && <TableCell>Assignable</TableCell>}
            <TableCell>Supports Wellbeing</TableCell>
            <TableCell>Supports High Performance</TableCell>
            <TableCell>Member Count</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {coaches.length === 0 && (
            <TableRow>
              <TableCell colSpan={1000} style={{ textAlign: 'center' }}>
                No coaches found
              </TableCell>
            </TableRow>
          )}
          {coaches?.map((coach) => (
            <TableRow key={coach.id}>
              <TableCell>{coach.firstName}</TableCell>
              <TableCell>{coach.lastName}</TableCell>
              <TableCell>{coach.email}</TableCell>
              <TableCell>{getDisplayType(coach.coachData?.type)}</TableCell>
              <TableCell>{coach.coachData?.coachingStyle}</TableCell>
              <TableCell>
                {coach.coachData?.locations?.nodes
                  .map((location) => location.name)
                  .join(', ')}
              </TableCell>
              <TableCell>
                {coach.coachData?.supportedCountries
                  ?.map((country) => country.name)
                  .join(', ')}
              </TableCell>
              {clientId && (
                <TableCell>
                  <BooleanField
                    record={{
                      availableForAssignment:
                        coach.coachData?.coachClients?.edges?.find(
                          (edge) => edge.node.id === clientId,
                        )?.availableForAssignment ?? false,
                    }}
                    source="availableForAssignment"
                  />
                </TableCell>
              )}
              <TableCell>
                <BooleanField
                  record={{
                    supportsWellbeingCoaching:
                      coach.coachData?.coachClients?.edges?.find(
                        (edge) => edge.node.id === clientId,
                      )?.supportsWellbeingCoaching ?? false,
                  }}
                  source="supportsWellbeingCoaching"
                />
              </TableCell>
              <TableCell>
                <BooleanField
                  record={{
                    supportsHighPerformanceCoaching:
                      coach.coachData?.coachClients?.edges?.find(
                        (edge) => edge.node.id === clientId,
                      )?.supportsHighPerformanceCoaching ?? false,
                  }}
                  source="supportsHighPerformanceCoaching"
                />
              </TableCell>
              <TableCell>{coach.coachData?.coachUsers.count ?? 0}</TableCell>
              <TableCell>
                <ShowButton record={coach} resource="User" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* Above the footer so the footer's height keeps the sentinel off the
          bottom edge of the scroll — see PaginationWaypoint. */}
      {hasNextPage && (
        <PaginationWaypoint callback={loadMore} itemCount={coaches.length} />
      )}
      {hasNextPage && (
        <div style={{ textAlign: 'center' }}>
          {isFetchingMore ? 'Loading...' : 'Scroll to load more'}
        </div>
      )}
    </TableContainer>
  );
};
